/**
 * Savanto Search Loader
 *
 * Lightweight vanilla JS script (~5KB) that runs on host page.
 * Creates keyboard shortcut handler and manages iframe containing the widget.
 */

import { isSafeNavigationUrl } from '../utils/linkUtils';
import { createIframe, type IframeAPI } from './createIframe';
import { createTrigger, type TriggerButtonAPI } from './createTrigger';
import type { ChildToParentMessage, LoaderConfig, LoaderTheme, ParentToChildMessage, SavantoSearchAPI } from './types';

// Widget URL - can be overridden via config
const DEFAULT_WIDGET_URL = 'https://cdn.savanto.ai/search-widget.html';

// Note: In development, use widget URL: http://localhost:3002/widget.html

/**
 * Resolve the keyboard shortcut key for Cmd/Ctrl + <key>.
 *
 * The modifier chord is always Cmd (Mac) / Ctrl (Win/Linux) plus a single key,
 * so anything that isn't exactly one character is rejected and we fall back to
 * the documented default of 'k'. Comparison happens after lowercasing.
 */
export function normalizeKeyboardShortcut(configured: string | undefined | null): string {
  if (typeof configured === 'string' && configured.length === 1) {
    return configured.toLowerCase();
  }
  return 'k';
}

// Minimal view of the chat widget's global API (window.SavantoChatbot) — a
// subset of SavantoChatbotAPI in widgets/chatbot/src/loader/types.ts; keep the
// `ask`/`open` signatures in sync with it. Both are optional because the chat
// loader exposes a pre-init stub (only `init`) before its full API is wired up.
interface ChatbotGlobal {
  ask?: (text: string) => void;
  open?: () => void;
}

/**
 * Resolve the chat widget's global API if it's installed on the page.
 * Returns undefined when the chat widget isn't present, which is what gates
 * the "Ask Assistant" handoff.
 */
function getChatbotApi(): ChatbotGlobal | undefined {
  return (window as unknown as { SavantoChatbot?: ChatbotGlobal }).SavantoChatbot;
}

class SavantoSearchLoader {
  private config: LoaderConfig;
  private trigger: TriggerButtonAPI | null = null;
  private iframe: IframeAPI | null = null;
  private isOpen = false;
  private isReady = false;
  private isRendered = false;
  private pendingOpen = false;
  private pendingMessages: ParentToChildMessage[] = [];
  private widgetUrl: string;
  private askRecheckTimer: ReturnType<typeof setInterval> | null = null;
  private previouslyFocused: HTMLElement | null = null;
  // Held so `destroy()` can remove them — otherwise Cmd/Ctrl+K keeps toggling a
  // torn-down loader and any same-origin postMessage still dispatches into a
  // dead instance, and hosts that swap loaders (Shopify theme editor / HMR)
  // accumulate a listener per cycle.
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor(config: LoaderConfig) {
    this.config = config;
    this.widgetUrl = config.widgetUrl || DEFAULT_WIDGET_URL;

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  private initialize(): void {
    const isInlineMode = this.config.embedMode === 'inline';

    this.trigger = createTrigger(this.config, () => this.toggle());

    this.iframe = createIframe(this.widgetUrl, this.config, () => this.onIframeLoad());

    this.setupMessageBridge();

    if (!isInlineMode) {
      this.setupKeyboardShortcut();
    }

    this.exposeGlobalAPI();

    if (isInlineMode) {
      this.isOpen = true;
    } else if ((window as unknown as Record<string, unknown>).__savantoPendingOpen) {
      delete (window as unknown as Record<string, unknown>).__savantoPendingOpen;
      const fallbackTimer = (window as unknown as Record<string, unknown>).__savantoSearchFallbackTimer;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer as ReturnType<typeof setTimeout>);
        delete (window as unknown as Record<string, unknown>).__savantoSearchFallbackTimer;
      }
      this.open();
    }
  }

  private onIframeLoad(): void {
    // Send initial config to iframe
    this.sendMessage({ type: 'config', payload: this.resolveConfig() });
    // Inline embeds set isOpen at init and never call showWidget() again, so —
    // unlike the floating overlay, which re-resolves on every open() — they
    // can't pick up a chat widget that bootstraps after this first send. Poll
    // briefly so "Ask Assistant" still flips on. No-op when it's off / already
    // available, or for non-inline embeds.
    if (this.config.embedMode === 'inline') {
      this.scheduleAskAssistantRecheck();
    }
  }

  // Bounded poll for the chat widget becoming available after our initial
  // config send (see onIframeLoad). Resends config once chat is live, then
  // stops; gives up after the window so it can't poll forever.
  private scheduleAskAssistantRecheck(): void {
    if (!this.config.askAssistant || this.isChatAvailable()) return;
    let attempts = 0;
    const maxAttempts = 20; // ~10s at 500ms — covers a slow chat bootstrap
    this.askRecheckTimer = setInterval(() => {
      attempts += 1;
      if (this.isChatAvailable()) {
        this.clearAskRecheck();
        this.sendMessage({ type: 'config', payload: this.resolveConfig() });
      } else if (attempts >= maxAttempts) {
        this.clearAskRecheck();
      }
    }, 500);
  }

  private clearAskRecheck(): void {
    if (this.askRecheckTimer) {
      clearInterval(this.askRecheckTimer);
      this.askRecheckTimer = null;
    }
  }

  private setupMessageBridge(): void {
    this.messageHandler = (event: MessageEvent) => {
      // Validate origin
      const widgetOrigin = new URL(this.widgetUrl).origin;
      if (event.origin !== widgetOrigin && event.origin !== window.location.origin) {
        return;
      }

      const message = event.data as ChildToParentMessage;
      if (!message || typeof message.type !== 'string') return;

      this.handleChildMessage(message);
    };
    window.addEventListener('message', this.messageHandler);
  }

  private handleChildMessage(message: ChildToParentMessage): void {
    switch (message.type) {
      case 'ready':
        this.isReady = true;
        // Send any pending messages
        for (const msg of this.pendingMessages) {
          this.iframe?.postMessage(msg);
        }
        this.pendingMessages = [];
        // If widget should be open, tell it
        if (this.isOpen) {
          this.sendMessage({ type: 'open' });
        }
        break;

      case 'requestClose':
        if (this.config.embedMode !== 'inline') {
          this.close();
        }
        break;

      case 'loaded':
        this.isRendered = true;
        if (this.config.debugMode) {
          console.log('[SavantoSearch] Widget loaded');
        }
        // If user tried to open before widget was ready, show it now
        if (this.pendingOpen) {
          this.pendingOpen = false;
          this.showWidget();
        }
        break;

      case 'resultClick':
        // The URL is indexed crawl content arriving via postMessage —
        // refuse to navigate the host to `javascript:` / `data:` etc.
        if (message.payload.url && isSafeNavigationUrl(message.payload.url)) {
          if (this.config.resultLinkTarget === '_blank') {
            window.open(message.payload.url, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = message.payload.url;
          }
        }
        break;

      case 'askAssistant': {
        // Hand off to the chat widget on the host page. The iframe only shows
        // this button when we told it chat was available, but re-check here
        // since the global is the source of truth at click time.
        const chatbot = getChatbotApi();
        if (!chatbot) break;
        const query = message.payload.query?.trim();
        // Only tear down the search if we can actually hand off — otherwise a
        // chat global that's lost its ask/open (e.g. a leftover stub) would
        // leave the visitor with no search and no chat.
        const willHandoff = (!!query && typeof chatbot.ask === 'function') || typeof chatbot.open === 'function';
        if (!willHandoff) break;
        // Floating overlay closes behind the chat; inline embeds stay put (the
        // chat opens over the page), mirroring the requestClose guard above.
        if (this.config.embedMode !== 'inline') {
          this.close();
        }
        if (query && chatbot.ask) {
          chatbot.ask(query);
        } else {
          chatbot.open?.();
        }
        break;
      }
    }
  }

  // Whether the chat widget can actually receive a handoff. Gates the
  // "Ask Assistant" button so it never renders with nothing to hand off to.
  // Checks for a callable `ask` rather than mere presence of the global: the
  // pre-bootstrap / `data-manual` stub exposes window.SavantoChatbot with only
  // `init`, which would otherwise show a button whose click does nothing.
  private isChatAvailable(): boolean {
    return typeof getChatbotApi()?.ask === 'function';
  }

  // Config forwarded to the iframe, with `askAssistant` forced off unless the
  // chat widget is actually present.
  private resolveConfig(): LoaderConfig {
    return {
      ...this.config,
      askAssistant: !!this.config.askAssistant && this.isChatAvailable(),
    };
  }

  private sendMessage(message: ParentToChildMessage): void {
    if (this.isReady && this.iframe) {
      this.iframe.postMessage(message);
    } else {
      this.pendingMessages.push(message);
    }
  }

  private setupKeyboardShortcut(): void {
    const shortcutKey = normalizeKeyboardShortcut(this.config.keyboardShortcut);

    this.keydownHandler = (e: KeyboardEvent) => {
      // Cmd+<key> (Mac) or Ctrl+<key> (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === shortcutKey) {
        e.preventDefault();
        this.toggle();
      }
    };
    document.addEventListener('keydown', this.keydownHandler);
  }

  private showWidget(): void {
    this.iframe?.show();
    // Re-resolve config on each open so a chat widget that finished
    // bootstrapping after our initial config send flips "Ask Assistant" on.
    // The two embed scripts load independently, so chat may not have been
    // present when onIframeLoad() sent the first config. (Inline embeds never
    // reach here, so they get the same refresh via scheduleAskAssistantRecheck.)
    this.sendMessage({ type: 'config', payload: this.resolveConfig() });
    this.sendMessage({ type: 'open' });

    // Pull focus into the iframe frame so the widget's input autofocus lands.
    // On reopen the host page holds focus (the Cmd+K / trigger gesture fires in
    // the host frame), and focusing an input from inside an unfocused
    // cross-origin frame can't steal frame focus — so do it from the parent,
    // which has the gesture. (First open works without this only because a
    // freshly-loaded iframe takes focus on its own.)
    this.iframe?.focus();

    // Lock body scroll
    document.body.style.overflow = 'hidden';
  }

  // Public API methods

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    // Remember what had focus on the host page so we can restore it on close
    // (standard modal behaviour). Captured before showWidget() moves focus into
    // the iframe. Works whether the opener was the trigger, Cmd+K, or the API.
    this.previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.trigger?.hide();

    // Only show if widget has rendered
    if (this.isRendered) {
      this.showWidget();
    } else {
      this.pendingOpen = true;
    }
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.sendMessage({ type: 'close' });

    // Unlock body scroll
    document.body.style.overflow = '';

    // Capture synchronously and clear the field now: the hide callback runs
    // ~200ms later, and a reopen within that window would otherwise overwrite
    // previouslyFocused (restoring the wrong element) or null it out so the
    // reopened session can't restore on its own close.
    const toRestore = this.previouslyFocused;
    this.previouslyFocused = null;

    // Hide iframe
    this.iframe?.hide(() => {
      this.trigger?.show();
      // Return focus to the opener — but not if the user reopened mid-hide, in
      // which case focus belongs in the re-shown overlay. focus() on a
      // detached/unfocusable element is a harmless no-op.
      if (!this.isOpen) {
        toRestore?.focus();
      }
    });
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  search(query: string): void {
    this.sendMessage({ type: 'search', payload: query });
    if (!this.isOpen) {
      this.open();
    }
  }

  isWidgetOpen(): boolean {
    return this.isOpen;
  }

  getConfig(): LoaderConfig {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<LoaderConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.sendMessage({ type: 'config', payload: this.resolveConfig() });
  }

  setTheme(theme: LoaderTheme): void {
    this.config.theme = { ...this.config.theme, ...theme };
    this.sendMessage({ type: 'theme', payload: theme });
  }

  destroy(): void {
    this.clearAskRecheck();
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
    this.trigger?.destroy();
    this.iframe?.destroy();
    this.trigger = null;
    this.iframe = null;
    document.body.style.overflow = '';
    delete (window as unknown as Record<string, unknown>).SavantoSearch;
  }

  private exposeGlobalAPI(): void {
    const api: SavantoSearchAPI = {
      open: () => this.open(),
      close: () => this.close(),
      toggle: () => this.toggle(),
      isOpen: () => this.isWidgetOpen(),
      search: (query: string) => this.search(query),
      getState: () => ({
        isOpen: this.isOpen,
        config: this.getConfig(),
      }),
      getConfig: () => this.getConfig(),
      configure: (config: Partial<LoaderConfig>) => {
        this.updateConfig(config);
        return true;
      },
      setTheme: (theme: LoaderTheme) => {
        this.setTheme(theme);
        return true;
      },
      setCustomCSS: (css: string) => {
        this.updateConfig({ customCSS: css });
        return true;
      },
      version: '1.0.0',
      isInitialized: () => true,
      destroy: () => this.destroy(),
    };

    (window as unknown as { SavantoSearch: SavantoSearchAPI }).SavantoSearch = api;
  }
}

/**
 * Parse data attributes from the script tag
 */
function getConfigFromDataAttributes(): Partial<LoaderConfig> {
  const script =
    document.currentScript ||
    document.querySelector('script[src*="savanto"][src*="search"]') ||
    document.querySelector('script[data-publishable-key]');

  if (!script) return {};

  const config: Partial<LoaderConfig> = {};

  const attrMap: Record<string, keyof LoaderConfig> = {
    'publishable-key': 'publishableKey',
    'workspace-id': 'workspaceId',
    'api-url': 'apiUrl',
    'widget-url': 'widgetUrl',
    'search-products': 'searchProducts',
    'search-posts': 'searchPosts',
    'product-limit': 'productLimit',
    'post-limit': 'postLimit',
    'include-facets': 'includeFacets',
    'show-trigger': 'showTrigger',
    'trigger-position': 'triggerPosition',
    'embed-mode': 'embedMode',
    container: 'containerSelector',
    'search-on-submit': 'searchOnSubmit',
    'result-layout': 'resultLayout',
    'product-tab-label': 'productTabLabel',
    'post-tab-label': 'postTabLabel',
    'input-placeholder': 'inputPlaceholder',
    'ask-assistant': 'askAssistant',
    'ask-assistant-label': 'askAssistantLabel',
    debug: 'debugMode',
  };

  const booleanAttrs = [
    'searchProducts',
    'searchPosts',
    'includeFacets',
    'showTrigger',
    'searchOnSubmit',
    'askAssistant',
    'debugMode',
  ];
  const numberAttrs = ['productLimit', 'postLimit'];

  for (const [attr, prop] of Object.entries(attrMap)) {
    const value = script.getAttribute(`data-${attr}`);
    if (value !== null) {
      if (booleanAttrs.includes(prop)) {
        (config as Record<string, unknown>)[prop] = value !== 'false' && (value === 'true' || value === '');
      } else if (numberAttrs.includes(prop)) {
        const num = Number.parseInt(value, 10);
        if (!Number.isNaN(num)) {
          (config as Record<string, unknown>)[prop] = num;
        }
      } else {
        (config as Record<string, unknown>)[prop] = value;
      }
    }
  }

  return config;
}

function applyConfigDefaults(config: Partial<LoaderConfig>): LoaderConfig {
  const result = { ...config } as LoaderConfig;

  if (result.publishableKey && !result.apiUrl) {
    result.apiUrl = 'https://api.savanto.ai';
  }

  if (result.publishableKey && !result.widgetUrl) {
    result.widgetUrl = DEFAULT_WIDGET_URL;
  }

  if (!result.workspaceId) {
    // Convert URL to normalized workspace ID (e.g., "example.com" → "example-com")
    // Strip www. prefix to match workspace created during crawl
    const hostname = new URL(window.location.origin).hostname.replace(/^www\./, '');
    result.workspaceId = hostname.replace(/\./g, '-').toLowerCase();
  }

  // Default search options
  if (result.searchProducts === undefined) result.searchProducts = true;
  if (result.searchPosts === undefined) result.searchPosts = true;
  if (result.includeFacets === undefined) result.includeFacets = true;

  return result;
}

/**
 * Fetch widget configuration from the cloud API
 * Always fetches fresh config to ensure immediate updates when settings change.
 * Browser HTTP caching with ETag headers provides efficiency.
 */
async function fetchRemoteConfig(
  publishableKey: string,
  workspaceId: string,
  apiUrl: string,
): Promise<Partial<LoaderConfig> | null> {
  try {
    const response = await fetch(`${apiUrl}/config/widget/search`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${publishableKey}`,
        'Content-Type': 'application/json',
        'X-Workspace-Id': workspaceId,
      },
    });

    if (!response.ok) {
      console.warn('[SavantoSearch] Failed to fetch widget config:', response.status);
      return null;
    }

    const result = await response.json();
    const remoteConfig = result.data;

    if (remoteConfig) {
      // Map remote config to LoaderConfig format
      const loaderConfig: Partial<LoaderConfig> = {};

      // Basic settings
      if (typeof remoteConfig.showTrigger === 'boolean') loaderConfig.showTrigger = remoteConfig.showTrigger;
      if (remoteConfig.triggerPosition) loaderConfig.triggerPosition = remoteConfig.triggerPosition;
      if (remoteConfig.keyboardShortcut) loaderConfig.keyboardShortcut = remoteConfig.keyboardShortcut;
      if (typeof remoteConfig.searchProducts === 'boolean') loaderConfig.searchProducts = remoteConfig.searchProducts;
      if (typeof remoteConfig.searchPosts === 'boolean') loaderConfig.searchPosts = remoteConfig.searchPosts;
      if (remoteConfig.customCSS) loaderConfig.customCSS = remoteConfig.customCSS;
      if (remoteConfig.resultLayout) loaderConfig.resultLayout = remoteConfig.resultLayout;
      if (typeof remoteConfig.searchOnSubmit === 'boolean') loaderConfig.searchOnSubmit = remoteConfig.searchOnSubmit;
      if (remoteConfig.productTabLabel) loaderConfig.productTabLabel = remoteConfig.productTabLabel;
      if (remoteConfig.postTabLabel) loaderConfig.postTabLabel = remoteConfig.postTabLabel;
      if (remoteConfig.inputPlaceholder) loaderConfig.inputPlaceholder = remoteConfig.inputPlaceholder;
      if (typeof remoteConfig.askAssistant === 'boolean') loaderConfig.askAssistant = remoteConfig.askAssistant;
      if (remoteConfig.askAssistantLabel) loaderConfig.askAssistantLabel = remoteConfig.askAssistantLabel;

      // Theme colors
      if (remoteConfig.theme) {
        const theme = remoteConfig.theme;
        const themeConfig: LoaderTheme = {
          primaryColor: theme.primaryColor,
          darkPrimaryColor: theme.darkPrimaryColor,
          colorScheme: theme.colorScheme,
        };

        if (theme.trigger) {
          themeConfig.trigger = {
            background: theme.trigger.background,
            textColor: theme.trigger.textColor,
            borderColor: theme.trigger.borderColor,
            darkBackground: theme.trigger.darkBackground,
            darkTextColor: theme.trigger.darkTextColor,
            darkBorderColor: theme.trigger.darkBorderColor,
          };
        }

        loaderConfig.theme = themeConfig;
      }

      return loaderConfig;
    }

    return null;
  } catch (error) {
    console.warn('[SavantoSearch] Error fetching widget config:', error);
    return null;
  }
}

async function initLoader(): Promise<void> {
  const dataAttrConfig = getConfigFromDataAttributes();
  const mergedConfig = { ...window.savantoSearchConfig, ...dataAttrConfig };
  let config = applyConfigDefaults(mergedConfig);

  // Check if settings are still loading (Shopify context)
  const settingsLoading = config && '_settingsLoaded' in config && !config._settingsLoaded;

  if (settingsLoading) {
    window.addEventListener('savanto:settings-loaded', async (event: Event) => {
      const customEvent = event as CustomEvent;
      let loadedConfig = applyConfigDefaults({ ...customEvent.detail?.config, ...dataAttrConfig });

      // Fetch remote config if we have a publishable key and workspace ID
      if (loadedConfig.publishableKey && loadedConfig.workspaceId && loadedConfig.apiUrl) {
        const remoteConfig = await fetchRemoteConfig(
          loadedConfig.publishableKey,
          loadedConfig.workspaceId,
          loadedConfig.apiUrl,
        );

        if (remoteConfig) {
          // Remote config is base, local config overrides
          loadedConfig = applyConfigDefaults({
            ...remoteConfig,
            ...customEvent.detail?.config,
            ...dataAttrConfig,
          });
        }
      }

      new SavantoSearchLoader(loadedConfig);
    });
  } else {
    // Fetch remote config if we have a publishable key and workspace ID
    if (config.publishableKey && config.workspaceId && config.apiUrl) {
      const remoteConfig = await fetchRemoteConfig(config.publishableKey, config.workspaceId, config.apiUrl);

      if (remoteConfig) {
        // Remote config is base, local config overrides
        config = applyConfigDefaults({
          ...remoteConfig,
          ...mergedConfig,
          ...dataAttrConfig,
        });
      }
    }

    // Initialize
    new SavantoSearchLoader(config);
  }
}

// Initialize
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLoader);
  } else {
    initLoader();
  }
}

export type { LoaderConfig, LoaderTheme, SavantoSearchAPI };
export { SavantoSearchLoader };
